import { useCallback, useEffect, useState } from "react";
import { ZoomIn } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { useReveal } from "@/lib/seo";
import { ZoomLightbox } from "@/components/ZoomLightbox";
import { Eyebrow } from "@/components/PageHero";
import { QuoteButton } from "@/components/QuoteButton";
import { MenuButton } from "@/components/MenuButton";

export function MenusSection({ showAllButton = true, limit = null, heading = "Des cartes gourmandes, africaines, créoles & françaises" }) {
  const [menus, setMenus] = useState([]);
  const [active, setActive] = useState(null);
  const close = useCallback(() => setActive(null), []);
  useReveal();

  useEffect(() => { api.get("/menus").then((r) => setMenus(r.data)).catch(() => {}); }, []);

  if (menus.length === 0) return null;
  const shown = limit ? menus.slice(0, limit) : menus;

  return (
    <section id="menus" className="bg-[#2C2C2C] py-24 lg:py-28" data-testid="menus-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="reveal mb-12 max-w-2xl">
          <Eyebrow>Nos menus & cartes</Eyebrow>
          <h2 className="font-serif text-3xl font-500 text-white lg:text-4xl">{heading}</h2>
          <p className="mt-4 text-sm leading-relaxed text-[#FAF6EF]/70">
            Cliquez sur une carte pour la consulter en détail. Nos menus sont ajustables et personnalisables selon votre projet — prestations sur devis.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((m, i) => (
            <button
              key={m.id}
              data-testid={`menu-card-${m.id}`}
              onClick={() => setActive(m)}
              style={{ transitionDelay: `${i * 80}ms` }}
              className="reveal group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] text-left transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-2xl"
            >
              <div className="aspect-[3/4] overflow-hidden">
                <img src={mediaUrl(m.url)} alt={m.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              </div>
              <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-[#2C2C2C] via-[#2C2C2C]/30 to-transparent p-5">
                <div className="flex items-center gap-2 text-[#C9A84C]">
                  <ZoomIn size={16} className="opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  <span className="text-xs uppercase tracking-wide opacity-0 transition-opacity duration-300 group-hover:opacity-100">Consulter</span>
                </div>
                <h3 className="mt-1 font-serif text-xl text-white">{m.title}</h3>
                {m.subtitle && <p className="text-xs text-[#FAF6EF]/70">{m.subtitle}</p>}
              </div>
            </button>
          ))}
        </div>

        {showAllButton && (
          <div className="reveal mt-10 flex justify-center">
            <MenuButton variant="gold" testId="menus-section-see-all">Voir toute notre carte</MenuButton>
          </div>
        )}

        {/* Rassurance */}
        <div className="reveal mt-14 rounded-2xl border border-[#C9A84C]/30 bg-white/[0.04] p-8 text-center" data-testid="menus-reassurance">
          <h3 className="font-serif text-2xl text-white lg:text-3xl">Vous avez une idée de menu ?</h3>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-[#FAF6EF]/75">
            Dites-nous ce dont vous rêvez : nous composons et réalisons votre carte sur mesure, selon vos goûts, vos convives et votre événement. Chaque menu est adaptable.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            <QuoteButton variant="gold" testId="menus-reassurance-quote">Réalisons votre menu</QuoteButton>
          </div>
        </div>
      </div>

      {active && <ZoomLightbox src={mediaUrl(active.url)} alt={active.title} onClose={close} />}
    </section>
  );
}
